import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type Entity } from 'components';
import { type OfferType } from 'models/offer';
import { type FetchOffersParams } from 'services/rtk/offers/offers';

export type OffersPageView = 'offers' | 'payouts';

export type OffersPageState = {
  view: OffersPageView; // Какие сущности отображать на странице: офферы или выплаты

  filters: {
    search?: string;
    category?: Entity;
    affiliate?: Entity;
    countryCode?: string;
    type?: OfferType;
    state?: 'archive' | 'active' | 'all';
    withWarranty?: boolean;
    isDirect?: boolean;
    onlyAdded?: boolean;
  };

  table: {
    page?: number;
    orderBy?: FetchOffersParams['orderBy'];
    sort?: 'asc' | 'desc';
  };
};

export const initialState: OffersPageState = {
  view: 'offers',
  filters: {
    state: 'active',
  },
  table: {
    page: 1,
  },
};

export const offersPageSlice = createSlice({
  name: 'offersFilters',
  initialState,

  reducers: {
    updateFilter: (
      state,
      action: PayloadAction<{
        field: Exclude<keyof OffersPageState['filters'], 'countryCode'>;
        value: OffersPageState['filters'][Exclude<keyof OffersPageState['filters'], 'countryCode'>];
      }>
    ) => {
      state.table.page = 1;

      state.filters[action.payload.field] = action.payload.value as never;
    },

    setCategory: (state, action: PayloadAction<Entity | undefined>) => {
      state.table.page = 1;

      // Если сбросили поле - переключить вид
      if (!action.payload?.id) {
        state.view = 'offers';
        state.table.orderBy = undefined;
        state.table.sort = undefined;
      } else if (!state.filters.category && state.filters.countryCode) {
        // Если заполнили category и уже заполнен countryCode - переключить режим
        state.view = 'payouts';
      }

      state.filters.category = action.payload;
    },

    setCountryCode: (state, action: PayloadAction<string | undefined>) => {
      state.table.page = 1;

      // Если сбросили поле - переключить вид
      if (!action.payload) {
        state.view = 'offers';
        state.table.orderBy = undefined;
        state.table.sort = undefined;
      } else if (!state.filters.countryCode && state.filters.category) {
        // Если заполнили countryCode и уже заполнен category - переключить режим
        state.view = 'payouts';
      }

      state.filters.countryCode = action.payload;
    },

    setAffiliate: (state, action: PayloadAction<Entity | undefined>) => {
      state.table.page = 1;

      state.filters.affiliate = action.payload;
    },

    setOrder: (
      state,
      action: PayloadAction<{
        orderBy?: FetchOffersParams['orderBy'];
        sort?: FetchOffersParams['sort'];
      }>
    ) => {
      state.table.page = 1;
      state.table.orderBy = action.payload.orderBy;
      state.table.sort = action.payload.sort;
    },

    setPage: (state, action: PayloadAction<number>) => {
      state.table.page = action.payload;
    },

    setView: (state, action: PayloadAction<OffersPageView>) => {
      if (state.view === 'payouts' && action.payload === 'offers') {
        state.table.orderBy = undefined;
        state.table.sort = undefined;
      }

      state.table.page = 1;

      state.view = action.payload;
    },

    setSort: (state, action: PayloadAction<OffersPageState['table']['sort']>) => {
      state.table.page = 1;
      state.table.sort = action.payload;
    },

    resetFilters: () => {
      return initialState;
    },
  },
});

export const {
  updateFilter,
  setCategory,
  setAffiliate,
  setCountryCode,
  setOrder,
  setPage,
  setView,
  setSort,
  resetFilters,
} = offersPageSlice.actions;

export const offersPageReducer = offersPageSlice.reducer;
